import type { Session } from "./schemas/session.js";
import { dateFormatter } from "./dateFormat.js";
import { formatMinutesLabel, sessionDurationMin, type DurationUnits } from "./duration.js";

// Pure grouping + label helpers for the Workouts history list. Kept free of
// native imports so they're unit-testable and shared by the mobile screen and
// the web-rnw console (the WorkoutsView lift, @heraion/ui). The screen
// keeps data loading, collapse state and render; this owns the month-section
// build and the locale-bound formatters.

// formatDayBadge runs once per list ROW on every render (and monthLabel per
// section header): the formatters come from dateFormatter, which caches one per
// (locale, shape) so the list scrolls without rebuilding them (perf A1).
export type WorkoutMonthSection = {
  key: string;
  total: number;
  data: Session[];
  collapsed: boolean;
};

// Build month sections from the loaded sessions. Key is "YYYY-MM" for
// stable sort; the most recent month auto-expands so the user sees
// their latest sessions on launch, other months collapse by default.
// `collapsedMonths` stores the user's toggle decisions: for the most
// recent month a presence flips it to collapsed; for any older month a
// presence flips it to expanded.
export function buildMonthSections(
  items: Session[],
  collapsedMonths: ReadonlySet<string>,
): WorkoutMonthSection[] {
  const byMonth = new Map<string, Session[]>();
  for (const s of items) {
    const at = new Date(s.startedAt);
    if (Number.isNaN(at.getTime())) continue;
    const key = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}`;
    const list = byMonth.get(key);
    if (list) list.push(s);
    else byMonth.set(key, [s]);
  }
  const keys = Array.from(byMonth.keys()).sort((a, b) => b.localeCompare(a));
  return keys.map((key, idx) => {
    const list = byMonth.get(key) ?? [];
    const isMostRecent = idx === 0;
    const userToggled = collapsedMonths.has(key);
    const collapsed = isMostRecent ? userToggled : !userToggled;
    return {
      key,
      total: list.length,
      data: collapsed ? [] : list,
      collapsed,
    };
  });
}

export function monthLabel(key: string, locale: string): string {
  const [yearStr, monthStr] = key.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(month)) return key;
  const fmt = dateFormatter(locale, { month: "long", year: "numeric" });
  return fmt.format(new Date(year, month, 1));
}

export function formatDayBadge(
  iso: string,
  locale: string,
): { day: string; dow: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: "—", dow: "" };
  const dayFmt = dateFormatter(locale, { day: "2-digit" });
  const dowFmt = dateFormatter(locale, { weekday: "short" });
  return {
    day: dayFmt.format(d),
    dow: dowFmt.format(d).toUpperCase(),
  };
}

// "Tue 29": short weekday + two-digit day, in the athlete's language (it:
// "Mar 29"). Used by the auto workout name (#367). The weekday is capitalised
// and loses the abbreviation dot some locales add ("mar." -> "Mar").
export function formatShortDayLabel(iso: string, locale: string): string {
  const { day, dow } = formatDayBadge(iso, locale);
  if (!dow) return "";
  const w = dow.toLowerCase().replace(/\.$/, "");
  // U+00A0: the name never wraps inside its own date.
  return `${w.charAt(0).toUpperCase()}${w.slice(1)}\u00A0${day}`;
}

/** Null when there is no measurable span; formatMinutesLabel owns the format. */
export function formatDurationLabel(
  started: string,
  ended: string | undefined,
  units: DurationUnits,
): string | null {
  const raw = sessionDurationMin(started, ended);
  return raw === null ? null : formatMinutesLabel(raw, units);
}
